import { message, Select, UploadProps, Switch } from "antd";
import { getCompanyGroups, getIndustryGrpLeaderResult } from "api/search.api";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebounce } from "utils";
import SectionTitleAndDescription from "./SectionTitleAndDescription";
import Label from "components/Common/Label/Label";
import SearchFilterDropdown from "components/Common/SearchableDropdown/SearchFilterDropdown";
import { Title5 } from "components/Common/Typography/Titles";
import { SvgIcons } from "Icon/SvgIcons";
import UploadFile from "components/Common/Input/UploadFile";
import Checkbox from "components/Common/Checkbox/Checkbox";
import { RootState } from "store/store";
import { setExcludeSubsidiaries, setEverWorked } from "store/slices/searcSlice";
import { fetchCompanies } from "utils/utils";

interface Company {
  name: string;
}

interface CompanySelectProps {
  title: string;
  dispatchAddAction: any;
  dispatchRemoveAction: any;
  isIncludeCompany: boolean;
  setIsIncludeCompany: any;
  placeholder: string;
  selectedCompanies: Company[];
  includedCompanies: Company[];
  dispatchGroupSelect: any;
}

const SelectExcludeCompanies: React.FC<CompanySelectProps> = ({
  title,
  dispatchGroupSelect,
  dispatchAddAction,
  dispatchRemoveAction,
  isIncludeCompany,
  setIsIncludeCompany,
  placeholder,
  selectedCompanies,
  includedCompanies,
}) => {
  const dispatch = useDispatch();
  const [totalPages, setTotalPages] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [csvData, setCsvData] = useState<string[]>([]);

  const [companiesGroup, setCompaniesGroup] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [allIndustries, setAllIndustries] = useState<{ value: string; label: string }[]>([]);

  const excludeSubsidiaries = useSelector((state: RootState) => state.search.exclude_subsidiaries);
  const everWorked = useSelector((state: RootState) => state.search.ever_worked);

  const getGroups = async () => {
    try {
      setLoading(true);
      const res = await getCompanyGroups(1, page);

      setCompaniesGroup((prevCompaniesGroup) => {
        const newGroups = res.results.filter(
          (newGroup: any) =>
            !prevCompaniesGroup.some((existingGroup) => existingGroup.id === newGroup.id)
        );
        return [...prevCompaniesGroup, ...newGroups];
      });

      setTotalPages(res.total_pages);
      if (page < res.total_pages) {
        setPage((prev) => prev + 1);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const props = {
    name: "file",
    accept: ".csv",
    customRequest: async ({ file, onSuccess }: { file: File; onSuccess: any }) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target?.result;
        if (typeof text === "string") {
          const rows = text.split(/\r?\n/).map((row) =>
            row.split(",").map((cell) => cell.trim().replace(/^"|"$/g, ""))
          );
          setCsvData(rows as any);
          onSuccess(rows);
        }
      };
      reader.readAsText(file);
    },
    onChange(info: any) {
      if (info.file.status === "done") {
        message.success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
  } as UploadProps;

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight &&
      !loading &&
      page < totalPages
    ) {
      getGroups();
    }
  };

  const handleCompanyGroupChange = (value: string) => {
    const selectedGroup = companiesGroup.find((group) => group.id === value);
    if (selectedGroup) {
      dispatch(dispatchGroupSelect(selectedGroup.companies.map((company: string) => company)));
    }
  };

  const handleUploadFile = (file: string[]) => {
    dispatch(dispatchGroupSelect(file.map((company: string) => company)));
  };

  const fetchIndustryLeaders = async (query: string) => {
    try {
      const response = await getIndustryGrpLeaderResult(query);
      const data: any = response;
      const matches = data?.data?.autocompleteCompanyIndustry?.matches || [];
      setAllIndustries(
        matches.map((item: any) => ({
          value: item.company,
          label: item.company,
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const debouncedSearchSkill = useDebounce((query: string) => {
    if (query) {
      fetchCompanies(query, setAllIndustries);
    }
  }, 500);

  const handleInputChange = (inputValue: string) => {
    debouncedSearchSkill(inputValue);
  };

  const handleDeselect = (value: string) => {
    dispatch(dispatchRemoveAction(value));
  };

  const handleCompanyChange = (selectedValues: string[]) => {
    const lastSelectedValue = selectedValues[selectedValues.length - 1];

    dispatch(dispatchAddAction(lastSelectedValue));
  };

  useEffect(() => {
    if (companiesGroup.length === 0) {
      getGroups();
    }
  }, []);

  useEffect(() => {
    handleUploadFile(csvData.flat());
  }, [csvData]);

  const handleCheck = () => {
    dispatch(setExcludeSubsidiaries(!excludeSubsidiaries));
  };

  const handleEverWorkedChange = (checked: boolean) => {
    dispatch(setEverWorked(checked));
  };

  return (
    <div className="mb-10">
      <SectionTitleAndDescription title={title} isRequiredSection={false} isHelpInfoReq={false} />
      <div className="flex flex-col border-2 rounded-lg border-main-border border-dashed p-7">
        <div className="xl:flex grid xl:gap-12 gap-5 pb-4 items-center">
          <div className="w-full">
            {/* <Label labelTitle={title} isHelpIconRequired tooltipText={"This is a tooltip"} /> */}
            <div className="SelectProject-input">
              <SearchFilterDropdown
                mode="multiple"
                options={allIndustries}
                onInputChange={handleInputChange}
                onChange={handleCompanyChange}
                defaultValue={selectedCompanies}
                handleDeselect={handleDeselect}
                placeholder={placeholder}
              />
            </div>
          </div>
        </div>
        <div
          className={`flex justify-between items-center overflow-hidden ${
            !isIncludeCompany ? "max-h-[1000px]" : "max-h-0"
          }`}
        >
          <p
            className="flex-1 text-sm text-primary font-normal mt-2 underline cursor-pointer"
            onClick={() => setIsIncludeCompany(true)}
          >
            Additional Filters
          </p>
          <div className="flex-1">
            <div className="flex space-x-2 mt-2.5 justify-end">
              <Checkbox
                checked={excludeSubsidiaries}
                onChange={handleCheck}
                inputId="excludeCompany"
                inputName="excludeCompany"
                inputValue="Bike"
                labelFor="excludeCompany"
                labelTitle="Include company subsidiaries"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm font-normal text-text-secondary">Exclude individuals who have ever worked at these companies</span>
          <Switch
            size="small"
            checkedChildren="Yes"
            unCheckedChildren="No"
            checked={everWorked}
            onChange={handleEverWorkedChange}
          />
        </div>
        <div
          className={`flex flex-col overflow-hidden transition-all ${
            isIncludeCompany ? "max-h-[1000px]" : "max-h-0"
          }`}
        >
          <div className="flex gap-2 justify-between">
            <Title5 text="Additional Filters" className="font-semibold mb-0" />
            <div className="flex items-center">
              {SvgIcons.intersectSquare}
              <Select
                placeholder="Company Group"
                variant="borderless"
                onPopupScroll={handlePopupScroll}
                style={{ flex: 1 }}
                onChange={handleCompanyGroupChange}
                options={companiesGroup.map((group) => ({
                  label: group.title,
                  value: group.id,
                }))}
              />
            </div>
          </div>
          <UploadFile {...props} primaryText="Click to upload a list of companies to exclude" />
          <div className="flex gap-2 justify-between mt-2">
            <Checkbox
              checked={excludeSubsidiaries}
              onChange={handleCheck}
              inputId="IncludeCompany"
              inputName="IncludeCompany"
              inputValue="Bike"
              labelFor="IncludeCompany"
              labelTitle="Exclude company subsidiaries"
            />
            <div className="flex items-center" onClick={() => setIsIncludeCompany(false)}>
              {SvgIcons.primaryCrossSvg}
              <p className="flex-1 text-sm text-primary font-normal cursor-pointer">Close Bulk</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectExcludeCompanies;
